import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const ContributorPortalPage = () => {
  // ALL HOOKS MUST BE AT THE TOP - UNCONDITIONALLY
  const navigate = useNavigate();
  const [selectedFeed, setSelectedFeed] = useState('home');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [megaLink, setMegaLink] = useState('');
  const [linkvertiseLink, setLinkvertiseLink] = useState('');
  const [premiumKeyLink, setPremiumKeyLink] = useState('');
  const [postType, setPostType] = useState('free');
  const [showRestrictionDialog, setShowRestrictionDialog] = useState(false);

  // CONDITIONAL LOGIC AFTER ALL HOOKS
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin' || user.role === 'super_admin';

  const handlePublish = () => {
    if (isAdmin) {
      // Admin users should use the admin dashboard
      navigate('/admin');
    } else {
      // For non-admin users, show restriction dialog
      setShowRestrictionDialog(true);
    }
  };

  const handleBack = () => {
    navigate('/profile');
  };

  const handleDialogBack = () => {
    setShowRestrictionDialog(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0A0A] to-[#1A1A1A]">
      {/* Header */}
      <div className="bg-[#1A1A1A]/80 backdrop-blur-sm border-b border-[#EE1C4E]/20 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-button"
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="text-gray-400 hover:text-white hover:bg-[#2A2A2A]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold text-white">Contributor Portal</h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Feed Selection */}
        <div className="space-y-2">
          <Label className="text-gray-300">Select Feed</Label>
          <Select value={selectedFeed} onValueChange={setSelectedFeed}>
            <SelectTrigger className="bg-[#2A2A2A] border-[#3A3A3A] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#2A2A2A] border-[#3A3A3A]">
              <SelectItem value="home" className="text-white hover:bg-[#3A3A3A]">HOME</SelectItem>
              <SelectItem value="eliteebony" className="text-white hover:bg-[#3A3A3A]">ELITEEBONY</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Post Type */}
        <div className="space-y-2">
          <Label className="text-gray-300">Post Type</Label>
          <Select value={postType} onValueChange={setPostType}>
            <SelectTrigger className="bg-[#2A2A2A] border-[#3A3A3A] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#2A2A2A] border-[#3A3A3A]">
              <SelectItem value="free" className="text-white hover:bg-[#3A3A3A]">Free</SelectItem>
              <SelectItem value="premium" className="text-white hover:bg-[#3A3A3A]">Premium</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Title */}
        <div className="space-y-2">
          <Label className="text-gray-300">Title</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter post title"
            className="bg-[#2A2A2A] border-[#3A3A3A] text-white placeholder:text-gray-500"
          />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label className="text-gray-300">Description</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter post description"
            rows={4}
            className="bg-[#2A2A2A] border-[#3A3A3A] text-white placeholder:text-gray-500 resize-none"
          />
        </div>

        {/* Image URL */}
        <div className="space-y-2">
          <Label className="text-gray-300">Image URL</Label>
          <Input
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="bg-[#2A2A2A] border-[#3A3A3A] text-white placeholder:text-gray-500"
          />
        </div>

        {/* Mega Folder Link */}
        <div className="space-y-2">
          <Label className="text-gray-300">Mega Folder Link</Label>
          <Input
            value={megaLink}
            onChange={(e) => setMegaLink(e.target.value)}
            placeholder="https://example.com/download..."
            className="bg-[#2A2A2A] border-[#3A3A3A] text-white placeholder:text-gray-500"
          />
        </div>

        {/* Linkvertise URL */}
        <div className="space-y-2">
          <Label className="text-gray-300">Linkvertise URL (Free Posts)</Label>
          <Input
            value={linkvertiseLink}
            onChange={(e) => setLinkvertiseLink(e.target.value)}
            placeholder="https://example.com/unlock..."
            className="bg-[#2A2A2A] border-[#3A3A3A] text-white placeholder:text-gray-500"
          />
        </div>

        {/* Premium Key Link */}
        <div className="space-y-2">
          <Label className="text-gray-300">Premium Key Link</Label>
          <Input
            value={premiumKeyLink}
            onChange={(e) => setPremiumKeyLink(e.target.value)}
            placeholder="https://example.com/key..."
            className="bg-[#2A2A2A] border-[#3A3A3A] text-white placeholder:text-gray-500"
          />
        </div>

        {/* Publish Button */}
        <Button
          onClick={handlePublish}
          className="w-full bg-[#EE1C4E] hover:bg-[#D91744] text-white font-semibold py-6 text-lg"
        >
          <Send className="w-5 h-5 mr-2" />
          Publish Post
        </Button>
      </div>

      {/* Restriction Dialog */}
      <Dialog open={showRestrictionDialog} onOpenChange={setShowRestrictionDialog}>
        <DialogContent className="bg-[#1A1A1A] border-[#3A3A3A] text-white">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Contribution Not Available</DialogTitle>
            <DialogDescription className="text-gray-400 text-base leading-relaxed pt-4">
              User contribution isn&apos;t available yet. You will be notified when it becomes available for public contribution.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button
              onClick={handleDialogBack}
              className="w-full bg-[#2A2A2A] hover:bg-[#3A3A3A] text-white"
            >
              Back to Portal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContributorPortalPage;
